import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { API } from '../../providers/ApiInterceptorProvider';
import { IPatchApplicationInProgress } from './types';

function getAxiosErrorFromCaught(err: unknown): AxiosError | null {
  if (err instanceof AxiosError) {
    return err;
  }
  if (err instanceof Error && err.cause instanceof AxiosError) {
    return err.cause;
  }
  return null;
}

interface ApiErrorResponseBody {
  message?: string;
  errorId?: string;
}

function buildMessageAndCaptureForSubmit(err: unknown, applicationId: string): { message: string } {
  const axiosErr = getAxiosErrorFromCaught(err);
  const status = axiosErr?.response?.status;
  const data = axiosErr?.response?.data as ApiErrorResponseBody | undefined;
  const message = data?.message ?? (status != null ? `Request failed with status ${status}` : 'Request failed');

  if (status != null && status >= 500) {
    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
      tags: {
        applicationId,
        operation: 'postApplicationInProgress',
      },
      extra: {
        applicationId,
        responseStatus: status,
        responseBody: data ?? axiosErr?.response?.data,
        errorId: data?.errorId,
      },
    });
  }

  return { message };
}

function buildMessageFromCaught(err: unknown): string {
  const axiosErr = getAxiosErrorFromCaught(err);
  const data = axiosErr?.response?.data as ApiErrorResponseBody | undefined;
  const status = axiosErr?.response?.status;
  return data?.message ?? (status != null ? `Request failed with status ${status}` : 'Request failed');
}

export const applicationFormApi = {
  getApplicationInProgress: async (applicationId: string) => {
    try {
      const response = await API.get<Record<string, string>>(`/application/${applicationId}/progress`);

      return response.data;
    } catch (err: unknown) {
      throw new Error(buildMessageFromCaught(err));
    }
  },
  patchApplicationInProgress: async ({
    questions,
    currentRoute,
    applicationId,
    deleteFollowingPages = false,
  }: IPatchApplicationInProgress) => {
    const body = {
      name: currentRoute,
      data: questions,
      deleteFollowingPages,
    };
    try {
      const response = await API.patch<Record<string, string>>(`/application/${applicationId}/progress`, body);

      return response.data;
    } catch (err: unknown) {
      throw new Error(buildMessageFromCaught(err));
    }
  },
  postApplicationInProgress: async (applicationId: string) => {
    try {
      const response = await API.post<Record<string, string>>(`/application/${applicationId}/submit`);

      return response.data;
    } catch (err: unknown) {
      const { message } = buildMessageAndCaptureForSubmit(err, applicationId);
      throw new Error(message);
    }
  },
};
